import React from 'react';
import { Helmet } from 'react-helmet';
import './legal.css';

const Terms = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Terms of Service — dmyor</title>
        <link rel="canonical" href="https://www.dmyor.com/terms" />
        <meta name="description" content="The terms that govern your use of dmyor." />
        <meta property="og:title" content="Terms of Service — dmyor" />
        <meta property="og:description" content="The terms that govern your use of dmyor." />
        <meta property="og:image" content="https://dmyor.com/images/og-image.png" />
        <meta property="og:url" content="https://www.dmyor.com/terms" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="legal-header">
        <div className="legal-eyebrow">dmyor</div>
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: 28 June 2026</p>
      </div>

      <div className="legal-body">
        <p>
          These Terms of Service ("Terms") govern your access to and use of dmyor (the
          "Service"). By accessing or using the Service, you agree to these Terms and to our{' '}
          <a href="/privacy-policy">Privacy Policy</a>. If you do not agree, do not use the Service.
        </p>

        <h2>1. Eligibility</h2>
        <p>
          You must be at least 16 years old (or the minimum age of digital consent in your
          country) and able to form a binding contract to use the Service. By using dmyor, you
          confirm you meet these requirements.
        </p>

        <h2>2. Your account</h2>
        <p>
          You can create an account with email and password or by signing in with Google. You are
          responsible for keeping your credentials secure and for all activity under your account.
          Notify us promptly of any unauthorised use. The information you provide must be accurate.
        </p>

        <h2>3. The Service</h2>
        <p>
          dmyor is a planning and execution system for turning long-term visions into milestones,
          actions, habits, documents, and a documented journey, with optional AI assistance. We
          may add, change, or remove features at any time.
        </p>

        <h2>4. AI features and no professional advice</h2>
        <p>
          The Service includes AI features ("Dmyor Assist") that generate suggestions, plans,
          breakdowns, summaries, and documents. AI output can be inaccurate, incomplete, or
          unsuitable for your situation. It is provided for general informational and planning
          purposes only and is <strong>not professional advice</strong> (including legal,
          financial, medical, or career advice). You are responsible for evaluating and deciding
          how to act on any output. Do not rely on it as a substitute for a qualified professional.
        </p>

        <h2>5. Your content</h2>
        <p>
          You retain ownership of the content you create on dmyor. You grant us a non-exclusive,
          worldwide, royalty-free licence to host, store, reproduce, process (including sending it
          to AI providers when you use AI features), and display your content solely to operate and
          improve the Service. If you choose to release or make content public, you grant us a
          licence to display it publicly as part of the Service and its public archive until you
          remove it. You are responsible for the content you create and share, and you confirm you
          have the rights to it.
        </p>

        <h2>6. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Post content that is unlawful, infringing, harmful, or that violates others' rights.</li>
          <li>Harass, impersonate, defame, or harm others, or post hateful or abusive content.</li>
          <li>Attempt to access accounts or data that are not yours, or breach security.</li>
          <li>Use bots, scrapers, or automated means to access the Service without permission.</li>
          <li>Misuse AI features to generate illegal, harmful, or abusive content.</li>
          <li>Interfere with or disrupt the integrity or performance of the Service.</li>
        </ul>

        <h2>7. Credits and purchases</h2>
        <p>
          Some features use credits, which may be purchased. Except where a refund is required by
          applicable law, credit purchases are final and non-refundable. You are responsible for
          managing and securing your credits. Pricing and credit terms may change going forward.
          See our <a href="/refund-policy">Refund Policy</a> for details.
        </p>

        <h2>8. Third-party services</h2>
        <p>
          The Service relies on third parties (for example Google for sign-in, AI providers,
          hosting, email, and payment providers). Your use of those features may also be subject to
          the third party's terms. We are not responsible for third-party services.
        </p>

        <h2>9. Intellectual property</h2>
        <p>
          The Service, including its software, design, and branding, is owned by dmyor and
          protected by intellectual property laws. These Terms do not grant you any right to our
          trademarks or to copy or reverse engineer the Service.
        </p>

        <h2>10. Disclaimers</h2>
        <p>
          The Service is provided "as is" and "as available", without warranties of any kind,
          whether express or implied, including merchantability, fitness for a particular purpose,
          and non-infringement. We do not warrant that the Service will be uninterrupted, secure,
          or error-free, or that any outcome or goal will be achieved.
        </p>

        <h2>11. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, dmyor and its affiliates will not be liable for
          any indirect, incidental, special, consequential, or punitive damages, or for any loss
          of profits, data, goodwill, or opportunities, arising from or related to your use of the
          Service. To the maximum extent permitted by law, our total liability for any claim will
          not exceed the greater of the amount you paid us in the 12 months before the claim or
          USD 50. Some jurisdictions do not allow certain limitations, so some of these may not
          apply to you.
        </p>

        <h2>12. Indemnification</h2>
        <p>
          You agree to indemnify and hold dmyor harmless from any claims, damages, and expenses
          (including reasonable legal fees) arising from your content, your use of the Service, or
          your violation of these Terms or applicable law.
        </p>

        <h2>13. Termination</h2>
        <p>
          You may stop using the Service and delete your account at any time. We may suspend or
          terminate your access if you violate these Terms or engage in harmful behaviour, or as
          needed to comply with the law or protect the Service and its users.
        </p>

        <h2>14. Governing law and disputes</h2>
        <p>
          These Terms are governed by the laws of India, without regard to conflict-of-law rules,
          and the courts located in India will have jurisdiction, except where mandatory local
          consumer-protection laws in your country of residence give you additional rights or a
          different forum.
        </p>

        <h2>15. Changes to these Terms</h2>
        <p>
          We may update these Terms from time to time. We will post the updated version here with a
          new "Last updated" date and, for significant changes, notify you by email or in-app.
          Continued use after changes means you accept the updated Terms.
        </p>

        <h2>16. Miscellaneous</h2>
        <p>
          If any provision is found unenforceable, the rest remains in effect. Our failure to
          enforce a provision is not a waiver. These Terms, together with the Privacy Policy and
          Refund Policy, are the entire agreement between you and dmyor regarding the Service.
        </p>

        <h2>17. Contact</h2>
        <p>
          For questions about these Terms, contact us at{' '}
          <a href="mailto:support@dmyor.com">support@dmyor.com</a>.
        </p>
      </div>
    </div>
  );
};

export default Terms;
